/**
 * RBAC (Role-Based Access Control) Type Definitions
 *
 * These types match the backend RBAC system defined in:
 * - docs/projects/rbac/03-design-doc.md (Section 4 - Frontend Integration)
 * - shippingtree/rbac/models.py
 */

// Business segments that can be protected
export type BusinessSegment =
  | 'outbound'
  | 'inbound'
  | 'inventory'
  | 'products'
  | 'vas'
  | 'billing'
  | 'postage'
  | 'integrations'
  | 'settings'
  | 'reports'

// Access levels in ascending order of privilege
export type FunctionRole = 'none' | 'viewer' | 'operator' | 'admin'

// Permission level for a single segment with source tracking
export interface SegmentPermission {
  level: FunctionRole
  source: 'role' | 'override'
  override_id?: number
}

// Role reference
export interface RoleRef {
  id: number
  name: string
}

// Warehouse scope (for warehouse users)
export interface WarehouseScope {
  scope_type: 'global' | 'warehouse'
  warehouse_id: number | null
  warehouse_name: string
  role: RoleRef | null
  segments: Record<BusinessSegment, SegmentPermission>
}

// Company scope (for merchant users)
export interface MerchantScope {
  company_id: number
  company_name: string
  role: RoleRef | null
  segments: Record<BusinessSegment, SegmentPermission>
}

// Warehouse user permissions from backend
export interface WarehousePermissionsResponse {
  user_type: 'warehouse'
  scopes: WarehouseScope[]
}

// Merchant user permissions from backend
export interface MerchantPermissionsResponse {
  user_type: 'merchant'
  scope: MerchantScope | null
}

// Discriminated union for backend response
export type RBACPermissionsResponse = WarehousePermissionsResponse | MerchantPermissionsResponse

// Frontend permission state for warehouse users
export interface WarehousePermissionState {
  userType: 'warehouse'
  // Map of WarehouseId -> Segment -> Level (all warehouses loaded upfront)
  // Key is warehouse ID as string, or 'global' for global permissions
  warehousePermissions: Record<string, Record<BusinessSegment, FunctionRole>>
  // Global permissions (for users with global roles, warehouse_id=null in DB)
  globalPermissions: Record<BusinessSegment, FunctionRole>
  // Current active warehouse ID (from warehouse switcher)
  activeWarehouseId: number | null
  // Role info per scope
  roles: Record<string, RoleRef | null>
}

// Frontend permission state for merchant users
export interface MerchantPermissionState {
  userType: 'merchant'
  // Company context (always set for merchants)
  companyId: number | null
  companyName: string | null
  // Role for the company
  role: RoleRef | null
  // Segment permissions for their company
  permissions: Record<BusinessSegment, FunctionRole>
}

// Discriminated union for frontend state
export type PermissionState = WarehousePermissionState | MerchantPermissionState | null

// Default empty permissions (all segments set to 'none')
export const EMPTY_PERMISSIONS: Record<BusinessSegment, FunctionRole> = {
  outbound: 'none',
  inbound: 'none',
  inventory: 'none',
  products: 'none',
  vas: 'none',
  billing: 'none',
  postage: 'none',
  integrations: 'none',
  settings: 'none',
  reports: 'none',
}

// All business segments for iteration
export const ALL_SEGMENTS: BusinessSegment[] = [
  'outbound',
  'inbound',
  'inventory',
  'products',
  'vas',
  'billing',
  'postage',
  'integrations',
  'settings',
  'reports',
]

// Level hierarchy for comparison (higher number = more access)
export const LEVEL_HIERARCHY: Record<FunctionRole, number> = {
  none: 0,
  viewer: 1,
  operator: 2,
  admin: 3,
}

/**
 * Check if one access level meets or exceeds another
 * @param actual The user's actual access level
 * @param required The minimum required access level
 * @returns true if actual >= required
 */
export function meetsAccessLevel(actual: FunctionRole, required: FunctionRole): boolean {
  return LEVEL_HIERARCHY[actual] >= LEVEL_HIERARCHY[required]
}

/**
 * Transform backend RBAC response to frontend permission state
 */
// =============================================================================
// Admin API Types - For Permission Management UI
// =============================================================================

/**
 * Role as returned from GET /api/rbac/roles/
 */
export interface RoleListItem {
  id: number
  name: string
  description: string
  user_type: 'warehouse' | 'merchant'
  default_scope: 'global' | 'warehouse' | 'company'
  is_system: boolean
  level: number
  company: number | null
  user_count: number
  permissions: Array<{
    segment: BusinessSegment
    access_level: FunctionRole
  }>
}

/**
 * Warehouse user role assignment from GET /api/rbac/users/{id}/roles/
 */
export interface WarehouseUserRoleAssignment {
  id: number
  role: number
  role_name: string
  warehouse: number | null
  warehouse_name: string | null
  scope_type: 'global' | 'warehouse'
  assigned_at: string
}

/**
 * Merchant user role assignment from GET /api/rbac/users/{id}/roles/
 */
export interface MerchantUserRoleAssignment {
  id: number
  role: number
  role_name: string
  company: number
  company_name: string
  assigned_at: string
}

export type UserRoleAssignment = WarehouseUserRoleAssignment | MerchantUserRoleAssignment

/**
 * Warehouse user override from GET /api/rbac/users/{id}/overrides/
 */
export interface WarehouseUserOverride {
  id: number
  segment: BusinessSegment
  access_level: FunctionRole
  warehouse: number | null
  warehouse_name: string | null
  scope_type: 'global' | 'warehouse'
  reason: string
  created_by: number
  created_at: string
}

/**
 * Merchant user override from GET /api/rbac/users/{id}/overrides/
 */
export interface MerchantUserOverride {
  id: number
  segment: BusinessSegment
  access_level: FunctionRole
  company: number
  company_name: string
  reason: string
  created_by: number
  created_at: string
}

export type UserOverride = WarehouseUserOverride | MerchantUserOverride

/**
 * Effective permissions scope for warehouse users
 */
export interface WarehouseEffectiveScope {
  scope_type: 'global' | 'warehouse'
  warehouse_id: number | null
  warehouse_name: string
  role: {
    id: number
    name: string
  } | null
  segments: Record<
    BusinessSegment,
    {
      level: FunctionRole
      source: 'role' | 'override'
      override_id?: number
    }
  >
}

/**
 * Effective permissions response for warehouse users
 */
export interface WarehouseEffectivePermissions {
  user_type: 'warehouse'
  scopes: WarehouseEffectiveScope[]
}

/**
 * Effective permissions scope for merchant users
 */
export interface MerchantEffectiveScope {
  company_id: number
  company_name: string
  role: {
    id: number
    name: string
  } | null
  segments: Record<
    BusinessSegment,
    {
      level: FunctionRole
      source: 'role' | 'override'
      override_id?: number
    }
  >
}

/**
 * Effective permissions response for merchant users
 */
export interface MerchantEffectivePermissions {
  user_type: 'merchant'
  scope: MerchantEffectiveScope | null
}

export type EffectivePermissionsResponse = WarehouseEffectivePermissions | MerchantEffectivePermissions

/**
 * Request payload for assigning a role
 */
export interface AssignRoleRequest {
  role_id: number
  warehouse_id?: number | null
}

/**
 * Single operation in bulk override request
 */
export interface OverrideOperation {
  segment: BusinessSegment
  level: FunctionRole | null // null to delete
  reason?: string
  warehouse_id?: number | null
}

/**
 * Request payload for bulk override update
 */
export interface BulkOverrideRequest {
  operations: OverrideOperation[]
}

/**
 * Response from bulk override operation
 */
export interface BulkOverrideResponse {
  data: {
    operations: Array<{
      segment: BusinessSegment
      status: 'created' | 'updated' | 'deleted' | 'no_change' | 'error'
      message?: string
      override_id?: number
    }>
  }
}

/**
 * Warehouse info for scope selection
 */
export interface WarehouseOption {
  id: number
  name: string
}

/**
 * Human-readable labels for segments
 */
export const SEGMENT_LABELS: Record<BusinessSegment, string> = {
  outbound: 'Outbound',
  inbound: 'Inbound',
  inventory: 'Inventory',
  products: 'Products',
  vas: 'VAS',
  billing: 'Billing',
  postage: 'Postage',
  integrations: 'Integrations',
  settings: 'Settings',
  reports: 'Reports',
}

/**
 * Human-readable labels for access levels
 */
export const LEVEL_LABELS: Record<FunctionRole, string> = {
  none: 'None',
  viewer: 'Viewer',
  operator: 'Operator',
  admin: 'Admin',
}

/**
 * All access levels for iteration (in order)
 */
export const ALL_LEVELS: FunctionRole[] = ['none', 'viewer', 'operator', 'admin']

// =============================================================================
// Transform Functions
// =============================================================================

export function transformRBACResponse(response: RBACPermissionsResponse): PermissionState {
  if (response.user_type === 'warehouse') {
    const warehousePermissions: Record<string, Record<BusinessSegment, FunctionRole>> = {}
    let globalPermissions: Record<BusinessSegment, FunctionRole> = { ...EMPTY_PERMISSIONS }
    const roles: Record<string, RoleRef | null> = {}

    for (const scope of response.scopes) {
      const permissions: Record<BusinessSegment, FunctionRole> = { ...EMPTY_PERMISSIONS }
      for (const segment of ALL_SEGMENTS) {
        if (scope.segments[segment]) {
          permissions[segment] = scope.segments[segment].level
        }
      }

      if (scope.scope_type === 'global') {
        globalPermissions = permissions
        roles['global'] = scope.role
      } else if (scope.warehouse_id !== null) {
        warehousePermissions[scope.warehouse_id.toString()] = permissions
        roles[scope.warehouse_id.toString()] = scope.role
      }
    }

    return {
      userType: 'warehouse',
      warehousePermissions,
      globalPermissions,
      activeWarehouseId: null, // Will be set by warehouse switcher
      roles,
    }
  } else {
    // Merchant user
    if (!response.scope) {
      return {
        userType: 'merchant',
        companyId: null,
        companyName: null,
        role: null,
        permissions: { ...EMPTY_PERMISSIONS },
      }
    }

    const permissions: Record<BusinessSegment, FunctionRole> = { ...EMPTY_PERMISSIONS }
    for (const segment of ALL_SEGMENTS) {
      if (response.scope.segments[segment]) {
        permissions[segment] = response.scope.segments[segment].level
      }
    }

    return {
      userType: 'merchant',
      companyId: response.scope.company_id,
      companyName: response.scope.company_name,
      role: response.scope.role,
      permissions,
    }
  }
}
